import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Self,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormGroupDirective,
  FormGroupName,
  NgControl,
} from '@angular/forms';
import { DepreciationMethodSettings } from '@assetaccountant/assets/models/depreciation-method-settings';
import { getValidRateRequest } from '@assetaccountant/core/models/get-rate-api-model-request';
import { GetRateFromLifeApiModelResponse } from '@assetaccountant/core/models/get-rate-api-model-response';
import { DepreciationService } from '@assetaccountant/core/services/depreciation.service';
import { isNumeric, roundTo } from '@assetaccountant/core/utils/numeric.utils';
import {
  emptyValue,
  NgControlDirective,
  TypedAsyncValidatorFn,
  TypedFormControl,
} from '@assetaccountant/core/utils/typed-forms';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { DepreciationBasis } from '@assetaccountant/api/models/depreciation-basis';
import { AssetRegimeFormGroup } from '../depreciation-method-selector.component';

@Component({
  selector: 'app-autocalc-life',
  templateUrl: './autocalc-life.component.html',
  styleUrls: ['./autocalc-life.component.scss'],
  standalone: false,
})
export class AutocalcLifeComponent implements OnInit, OnDestroy, ControlValueAccessor {
  model: number | null = null;

  @Input() firstUseDate: string;
  @Input() effectiveDate?: string;
  @Input() set settings(value: DepreciationMethodSettings | undefined) {
    this.lifeValues = value?.lifeValues?.length ? value.lifeValues : undefined;
    this.isUnitsOfUse = value?.isUnitsOfUse ?? false;
    this.isRateApplicable = value?.isRateApplicable ?? false;
    this.isRateEditable = value?.isRateEditable ?? false;
    this.autoCalculateRate = value?.autoCalculateRate ?? false;
    this.depreciationBasis = value?.basis ?? DepreciationBasis.Value;
  }
  @Input() allowBlankSettings = false;

  @Output() remainingLifeChange = new EventEmitter<number | undefined>();

  isDisabled = false;

  lifeValues?: number[];
  isUnitsOfUse = false;
  isRateApplicable = false;
  isRateEditable = false;
  autoCalculateRate = false;
  depreciationBasis = DepreciationBasis.Value;

  get form() {
    return this.controlContainer.control as AssetRegimeFormGroup;
  }
  get rateControl() {
    return this.form.controls.rate;
  }
  get hasLookupLife() {
    return (
      !!this.form.controls.lookup.value?.life ||
      !!this.form.controls.lookup.value?.rates?.some(x => x.life)
    );
  }

  _onChange?: (val: number | null) => void;
  _onTouched?: () => void;

  constructor(
    @Inject(ControlContainer) private controlContainer: FormGroupDirective | FormGroupName,
    @Self() @Inject(NgControl) private ngControl: NgControlDirective,
    private depreciationService: DepreciationService,
  ) {
    this.ngControl.valueAccessor = this;
  }

  ngOnInit() {
    if (this.isRateEditable) {
      this.rateControl.enable();
    } else {
      this.rateControl.disable();
    }

    setTimeout(() => {
      this.ngControl.control.setAsyncValidators(c => this.validate(c as TypedFormControl<number>));
      this.ngControl.control.updateValueAndValidity();
    });
  }

  ngOnDestroy() {
    this.ngControl.control.clearAsyncValidators();
  }

  private updateModel(life: number | string | null) {
    if (isNumeric(life)) {
      this.model = +life;
    } else {
      this.model = null;
    }
  }

  writeValue(value: number | null) {
    this.updateModel(value);
  }

  onChange(value: number | string | null) {
    this.updateModel(value);
    this._onChange?.(this.model);
  }

  onBlur() {
    this._onTouched?.();
  }

  validate: TypedAsyncValidatorFn<TypedFormControl<number>> = control => {
    const life = control.value;
    return this.getRateAndLife(life).pipe(
      map(response => {
        if (!response) {
          return null;
        }

        // Has the value for life changed?
        if (this.model !== life) {
          return null;
        }

        const { rate, remainingLife } = response;

        // Update remaining life
        this.remainingLifeChange.emit(remainingLife);

        // Update rate if it should be calculated
        if (this.autoCalculateRate && this.form) {
          this.updateRateControl(rate);

          if (!isNumeric(rate)) {
            return {
              remainingLife: {
                life,
                remainingLife,
              },
            };
          }
        }

        return null;
      }),
    );
  };

  getRateAndLife(life: number | null): Observable<null | GetRateFromLifeApiModelResponse> {
    const { regimeId, depreciationMethodId } = this.form.value;
    if (isNaN(+depreciationMethodId)) {
      return of(null);
    }
    const request = getValidRateRequest({
      regimeId,
      depreciationMethodId: +depreciationMethodId,
      life,
      firstUseDate: this.firstUseDate,
      date: this.effectiveDate,
    });

    if (!request) {
      return of(null);
    }

    return this.depreciationService.getRate(request).pipe(
      map(response => {
        if (!response) {
          return null;
        }

        const { rate, remainingLife } = response;

        const roundedRemainingLife = remainingLife && roundTo(remainingLife, 4);

        return {
          rate,
          remainingLife: roundedRemainingLife,
        };
      }),
    );
  }

  private updateRateControl(rate: number | undefined) {
    if (!this.isRateApplicable || !this.autoCalculateRate) {
      return;
    }

    if (!this.isRateEditable || !this.rateControl.value || this.rateControl.pristine) {
      this.rateControl.setValue(rate || emptyValue<number>());
      this.rateControl.markAsPristine();
      this.rateControl.markAsTouched();
    }
  }

  registerOnChange(fn: (val: number | null) => void): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
}
